import React, { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { ChevronDown, Check } from "lucide-react";
import Style from "./writebackaccountfield.module.scss";

WritebackAccountField.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      AccountID: PropTypes.string,
      Code: PropTypes.string,
      Name: PropTypes.string
    })
  ),
  value: PropTypes.string,
  valueLabel: PropTypes.string,
  helperText: PropTypes.string,
  open: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired
};

export default function WritebackAccountField({
  label,
  options = [],
  value,
  valueLabel,
  helperText,
  open,
  disabled,
  readOnly,
  onToggle,
  onRequestClose,
  onSelect
}) {
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const optionRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const instanceId = useId();
  const optionId = (index) => `${instanceId}-option-${index}`;

  const selectedIndex = options.findIndex(
    (option) => option.AccountID === value
  );
  const triggerLabel =
    valueLabel ||
    (selectedIndex >= 0
      ? `${options[selectedIndex].Code} - ${options[selectedIndex].Name}`
      : "Select an account");

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        onRequestClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onRequestClose]);

  useEffect(() => {
    if (open) {
      // A truthy `value` with no matching option means the saved account was archived,
      // renamed, or reclassified in Xero since it was selected - falling back to 0 in
      // that case would highlight an unrelated live account, and a reflexive Enter to
      // dismiss the panel would silently commit it. Only default to the first option
      // when there's genuinely no prior selection (value is falsy) to preserve.
      let initialActiveIndex = 0;
      if (selectedIndex >= 0) {
        initialActiveIndex = selectedIndex;
      } else if (value) {
        initialActiveIndex = -1;
      }
      setActiveIndex(initialActiveIndex);
      // Move focus into the listbox so arrow-key/Enter/Escape handling below
      // actually receives the keystrokes - otherwise focus stays on the
      // trigger button and only mouse interaction works.
      panelRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (open && activeIndex >= 0) {
      optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [open, activeIndex]);

  const closeAndFocusTrigger = () => {
    onRequestClose();
    triggerRef.current?.focus();
  };

  const handleTriggerKeyDown = (event) => {
    if (disabled || readOnly) return;
    if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      if (!open) {
        onToggle();
      }
    } else if (event.key === "Escape" && open) {
      closeAndFocusTrigger();
    }
  };

  const handleListKeyDown = (event) => {
    // Scoped to the movement/commit keys only - Escape and Tab must still close the
    // panel when it's open on an empty options list (e.g. a transient Xero outage
    // degrading the picker to no options), otherwise the panel is stuck open with
    // aria-expanded=true and no way for a keyboard user to dismiss it with Escape.
    if (
      !options.length &&
      ["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)
    ) {
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((prev) => (prev + 1) % options.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((prev) => (prev - 1 + options.length) % options.length);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (activeIndex >= 0) {
          onSelect(options[activeIndex]);
          closeAndFocusTrigger();
        }
        break;
      case "Escape":
        event.preventDefault();
        closeAndFocusTrigger();
        break;
      case "Tab":
        // Focus the trigger before the panel unmounts, otherwise React removes the
        // focused node mid-event and the browser's default Tab action has nowhere
        // to move focus from, so it falls through to document.body instead of
        // advancing to the next field.
        triggerRef.current?.focus();
        onRequestClose();
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={`form-group d-flex flex-wrap align-items-start mb-3 ${Style.writebackRow}`}
    >
      <label className="col-12 col-md-5 pt-1 pb-1">{label}</label>
      <div
        className={`col-12 col-md-7 ${Style.writebackField}`}
        ref={wrapperRef}
      >
        <button
          type="button"
          ref={triggerRef}
          disabled={readOnly}
          className={`${Style.writebackTrigger} ${disabled ? Style.writebackTriggerBusy : ""}`}
          aria-label={label}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-busy={disabled}
          onClick={() => {
            // Not a native `disabled` attribute for the saving case - saving a field
            // synchronously flips `disabled` true (see AccountingWritebackFields.handleSelect)
            // while the just-clicked trigger/option still has focus. `disabled` on a focused
            // element makes the browser force-blur it to document.body per the HTML spec,
            // which a native attribute here can't avoid. Guarding the handler instead achieves
            // the same "ignore interaction while saving" behavior without ever stealing focus.
            // readOnly doesn't toggle while focused, so it's also enforced via the native
            // attribute above; this guard is defense in depth for that case.
            if (disabled || readOnly) return;
            onToggle();
          }}
          onKeyDown={handleTriggerKeyDown}
        >
          <span className={Style.writebackTriggerValue}>{triggerLabel}</span>
          <ChevronDown
            size={16}
            strokeWidth={2}
            className={Style.writebackChevron}
          />
        </button>

        {open && (
          <div
            ref={panelRef}
            role="listbox"
            tabIndex={-1}
            aria-label={label}
            aria-activedescendant={
              activeIndex >= 0 ? optionId(activeIndex) : undefined
            }
            className={Style.writebackPanel}
            onKeyDown={handleListKeyDown}
          >
            {options.map((option, index) => {
              const isSelected = option.AccountID === value;
              return (
                <button
                  key={option.AccountID}
                  id={optionId(index)}
                  ref={(el) => (optionRefs.current[index] = el)}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`${Style.writebackOption} ${
                    index === activeIndex ? Style.writebackOptionActive : ""
                  }`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => {
                    onSelect(option);
                    closeAndFocusTrigger();
                  }}
                >
                  <span className={Style.writebackOptionLabel}>
                    <span className={Style.writebackOptionCode}>
                      {option.Code}
                    </span>
                    {"  "}
                    {option.Name}
                  </span>
                  {isSelected && (
                    <Check
                      size={16}
                      strokeWidth={2.5}
                      className={Style.writebackCheck}
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {helperText && <p className={Style.writebackHelper}>{helperText}</p>}
      </div>
    </div>
  );
}
